/**
 * Midjourney (APIFrame) Configuration
 * ===================================
 * ONLY module in src/services/midjourney/ that accesses env transport URLs.
 * Mirrors the discipline of src/services/fal/config.js.
 *
 * APIFrame v2 (api.apiframe.ai). Keys start with `afk_`; auth is the raw
 * key in an `X-API-Key` header (no "Bearer", no `Authorization`) from the
 * Cloudflare Worker only. Verified live against the API on 2026-07-03.
 *
 * Transport modes:
 *   - Default: authenticated Worker proxy at /api/midjourney.
 *   - If VITE_FAL_PROXY_URL is configured, use that Worker origin and append
 *     /api/midjourney so Midjourney follows the same server-side media route.
 *   - Optional: VITE_APIFRAME_PROXY_URL can point at a custom full
 *     /api/midjourney route for experiments.
 *
 * The v2 path prefix (`/v2/...`) lives in the client's request paths, so
 * the Worker rewrite only strips the /api/midjourney prefix.
 *
 * APIFrame re-hosts result images on its own CDN (cdn2.apiframe.ai) with
 * `access-control-allow-origin: *`, so result URLs are returned to the app
 * directly (no image proxy needed). Verified live 2026-07-03.
 */

export const APIFRAME_WORKER_PROXY_BASE_PATH = '/api/midjourney';

let getAccessToken = null;

function normalizeProxyUrl(value) {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\/+$/, '');
}

function resolveApiFrameProxyBaseUrl(env = import.meta.env) {
  const explicitProxyUrl = normalizeProxyUrl(env?.VITE_APIFRAME_PROXY_URL);
  if (explicitProxyUrl) return explicitProxyUrl;

  const mediaProxyUrl = normalizeProxyUrl(env?.VITE_FAL_PROXY_URL);
  if (mediaProxyUrl) return `${mediaProxyUrl}${APIFRAME_WORKER_PROXY_BASE_PATH}`;

  return APIFRAME_WORKER_PROXY_BASE_PATH;
}

export function initializeApiFrameClient(accessTokenGetter) {
  getAccessToken = typeof accessTokenGetter === 'function' ? accessTokenGetter : null;
}

export function getApiFrameBaseUrl(
  env = import.meta.env,
) {
  return resolveApiFrameProxyBaseUrl(env);
}

export function getApiFrameAuthHeaders() {
  const token = typeof getAccessToken === 'function' ? getAccessToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}
